/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import style from "../../styles/navbar.module.scss";
import { BiPhoneCall } from "react-icons/bi";
import { IoCartOutline, IoSearchOutline } from "react-icons/io5";

import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo3.png";
import { Squash as Hamburger } from "hamburger-react";
import { IoMdClose } from "react-icons/io";
import { useCart } from "react-use-cart";
import { GlobalContext } from "../../context/GlobalContext";
import api, { imageUrl } from "../../baseApi";
import { categoryLink } from "../../utils/categoryLink";
import { useCartDrawer } from "../../context/CartDrawerContext";
import FreeShipping from "./FreeShipping";
import { unitPrice } from "../../utils/pricing";

const Navbar = () => {
  const { getSale, sale } = useContext(GlobalContext);
  const { items } = useCart();
  const { openDrawer } = useCartDrawer();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const requestId = useRef(0);
  const typed = useRef(false);
  const [isOpen, setOpen] = useState(false);
  const [navCategories, setNavCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const menuTimer = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    getSale();
    api
      .get("/categories", { params: { populate: "*" } })
      .then((response) => setNavCategories(response.data.data || []))
      .catch(() => setNavCategories([]));
  }, []);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    typed.current = false;
    setSuggestOpen(false);
    setOpenMenu(null);
    const params = new URLSearchParams(location.search);
    setQuery(location.pathname === "/shop" ? params.get("search") || "" : "");
  }, [location.pathname, location.search]);

  useEffect(() => () => clearTimeout(menuTimer.current), []);

  const scheduleOpen = (id) => {
    clearTimeout(menuTimer.current);
    if (!window.matchMedia("(hover: hover) and (min-width: 901px)").matches) {
      return;
    }
    menuTimer.current = setTimeout(() => setOpenMenu(id), 400);
  };

  const scheduleClose = () => {
    clearTimeout(menuTimer.current);
    menuTimer.current = setTimeout(() => setOpenMenu(null), 180);
  };

  useEffect(() => {
    if (!typed.current) return undefined;
    const term = query.trim();
    if (term.length < 2) {
      setSuggestions([]);
      setSuggestOpen(false);
      return undefined;
    }

    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      try {
        const response = await api.get("/products", {
          params: {
            populate: "*",
            "pagination[pageSize]": 5,
            "filters[Title][$containsi]": term,
          },
        });
        if (id !== requestId.current) return;
        setSuggestions(response.data.data || []);
        setSuggestOpen(true);
      } catch (error) {
        if (id === requestId.current) setSuggestions([]);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  const currentCategory = new URLSearchParams(location.search).get("category");

  const categoryMatches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < 2) return [];
    const hits = [];
    navCategories.forEach((category) => {
      if (category.name?.toLowerCase().includes(term)) {
        hits.push({
          key: `parent-${category.id}`,
          name: category.name,
          to: categoryLink(category),
        });
      }
      (category.subcategories || []).forEach((sub) => {
        if (sub.name?.toLowerCase().includes(term)) {
          hits.push({
            key: `sub-${sub.id || sub.slug}`,
            name: sub.name,
            hint: category.name,
            to: `/shop?category=${sub.slug}`,
          });
        }
      });
    });
    return hits.slice(0, 4);
  }, [query, navCategories]);

  const categoryIsActive = (category) => {
    if (location.pathname !== "/shop" || !currentCategory) return false;
    const slugs = (category.subcategories || [])
      .map((sub) => sub.slug)
      .filter(Boolean);
    const current = currentCategory.split(",").map((slug) => slug.trim());
    if (
      current.length === slugs.length &&
      current.every((slug) => slugs.includes(slug))
    ) {
      return true;
    }
    return current.length === 1 && slugs.includes(current[0]);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const term = query.trim();
    setSuggestOpen(false);
    setOpen(false);
    navigate(term ? `/shop?search=${encodeURIComponent(term)}` : "/shop");
  };

  const searchField = (
    <div className={style.search} ref={searchRef}>
      <form onSubmit={submitSearch}>
        <input
          type="search"
          value={query}
          placeholder="Kërko një produkt"
          aria-label="Kërko një produkt"
          onChange={(event) => {
            typed.current = true;
            setQuery(event.target.value);
            if (event.target.value.trim().length >= 2) setSuggestOpen(true);
          }}
          onFocus={() => {
            if (suggestions.length > 0 || categoryMatches.length > 0) {
              setSuggestOpen(true);
            }
          }}
        />
        <button type="submit" aria-label="Kërko">
          <IoSearchOutline size={20} />
          <span>Kërko</span>
        </button>
      </form>
      {suggestOpen ? (
        <ul className={style.suggestions}>
          {categoryMatches.map((match) => (
            <li key={match.key}>
              <Link to={match.to} onClick={() => setSuggestOpen(false)}>
                <span className={style.suggestKind}>Kategori</span>
                <span className={style.suggestName}>
                  {match.name}
                  {match.hint ? (
                    <span className={style.suggestHint}>{match.hint}</span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
          {suggestions.length === 0 && categoryMatches.length === 0 ? (
            <li className={style.emptySuggest}>Nuk u gjet asnjë produkt</li>
          ) : (
            suggestions.map((product) => (
              <li key={product.id}>
                {product.notInStock ? (
                  <div className={style.soldSuggest}>
                    {product.photos?.[0]?.url ? (
                      <img
                        src={`${imageUrl}${product.photos[0].url}`}
                        alt=""
                      />
                    ) : (
                      <span className={style.thumb} />
                    )}
                    <span className={style.suggestName}>{product.Title}</span>
                    <span className={style.suggestPrice}>E shitur</span>
                  </div>
                ) : (
                  <Link
                    to={product.slug ? `/shop/${product.slug}` : "/shop"}
                    onClick={() => setSuggestOpen(false)}
                  >
                    {product.photos?.[0]?.url ? (
                      <img
                        src={`${imageUrl}${product.photos[0].url}`}
                        alt=""
                      />
                    ) : (
                      <span className={style.thumb} />
                    )}
                    <span className={style.suggestName}>{product.Title}</span>
                    <span className={style.suggestPrice}>
                      {unitPrice(product).toFixed(2)}€
                    </span>
                  </Link>
                )}
              </li>
            ))
          )}
          <li>
            <Link
              to={
                query.trim()
                  ? `/shop?search=${encodeURIComponent(query.trim())}`
                  : "/shop"
              }
              className={style.seeAll}
              onClick={() => setSuggestOpen(false)}
            >
              Shiko të gjitha
            </Link>
          </li>
        </ul>
      ) : null}
    </div>
  );

  return (
    <div className={style.container}>
      <div className={style.orderLine}>
        <div className={style.innerContainer}>
          <a className={style.order} href="tel:+38348808287">
            <BiPhoneCall color="white" size={18} />
            +383 48 80 82 87
          </a>
          <span className={style.shippingNote}>
            <FreeShipping variant="text" />
          </span>
          {sale?.Sale?.show && sale?.Sale?.saleText ? (
            <Link className={style.saleLink} to="/shop?filter=discount">
              {sale.Sale.saleText}
            </Link>
          ) : null}
        </div>
      </div>
      <div className={style.navbar}>
        <div className={`${style.innerContainer} ${style.topBar}`}>
          <Link to="/" className={style.logoLink}>
            <img src={logo} alt="On Beauty" />
          </Link>
          {searchField}
          <div className={style.cart}>
            <button
              type="button"
              className={style.cartLink}
              aria-label="Shporta"
              onClick={openDrawer}
            >
              <IoCartOutline size={26} />
              {items.length > 0 ? (
                <span className={style.badge}>{items.length}</span>
              ) : null}
            </button>
            <div className={style.burger}>
              <Hamburger toggled={isOpen} toggle={setOpen} size={20} />
            </div>
          </div>
        </div>
        <div className={style.departments}>
          <div className={style.innerContainer}>
            <Link
              to="/shop"
              className={
                location.pathname === "/shop" && !currentCategory
                  ? style.active
                  : undefined
              }
            >
              Shop
            </Link>
            {navCategories.map((category) => {
              const to = categoryLink(category);
              const subs = category.subcategories || [];
              return (
                <div
                  key={category.id}
                  className={style.deptItem}
                  onMouseEnter={() => scheduleOpen(category.id)}
                  onMouseLeave={scheduleClose}
                >
                  <Link
                    to={to}
                    className={categoryIsActive(category) ? style.active : undefined}
                  >
                    {category.name}
                  </Link>
                  {openMenu === category.id && subs.length > 0 ? (
                    <div
                      className={`${style.mega} ${
                        subs.length > 6 ? style.wide : ""
                      }`}
                    >
                      <Link to={to} className={style.all}>
                        Të gjitha në {category.name}
                      </Link>
                      {subs.map((sub) => (
                        <Link
                          key={sub.id || sub.slug}
                          to={`/shop?category=${sub.slug}`}
                          className={
                            currentCategory === sub.slug ? style.active : undefined
                          }
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {isOpen ? (
        <div
          className={`${style.mobileNavbar}  animate__animated animate__fadeInDown`}
        >
          <IoMdClose
            size={25}
            onClick={() => {
              setOpen(false);
            }}
          />

          <Link
            to="/"
            className={style.logo}
            onClick={() => {
              setOpen(false);
            }}
          >
            <img src={logo} alt="On Beauty" />
          </Link>
          <div className={style.mobileLinks}>
            <Link to="/shop" onClick={() => setOpen(false)}>
              Shop
            </Link>
            {navCategories.map((category) => (
              <div className={style.mobileGroup} key={category.id}>
                <Link
                  className={style.mobileParent}
                  to={categoryLink(category)}
                  onClick={() => setOpen(false)}
                >
                  {category.name}
                </Link>
                {(category.subcategories || []).map((sub) => (
                  <Link
                    key={sub.id || sub.slug}
                    className={style.mobileSub}
                    to={`/shop?category=${sub.slug}`}
                    onClick={() => setOpen(false)}
                  >
                    {sub.name}
                  </Link>
                ))}
              </div>
            ))}
            <Link to="/about-us" onClick={() => setOpen(false)}>
              Rreth nesh
            </Link>
            <Link to="/contact-us" onClick={() => setOpen(false)}>
              Kontakt
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Navbar;
